/**
 * SSO Handler v2 — Peak Saver login → address eligibility → verified bar.
 * Persists `_dr_*` + offer stamps to cart attributes on enroll (discount Function
 * reads them); also injects into cart-form on checkout. Standalone <sso-handler>.
 *
 * FEATURE FLAGS: Theme.features.sso_enabled (global kill switch) and
 * Theme.features.peak_saver_sso_enabled (shop.mysa.peak_saver_sso_enabled).
 * This module is loaded only when the Peak Saver metafield is true.
 */

import { Component } from "@theme/component";
import { MysaEvents } from "@theme/mysa-events";
import { showDialogElement, closeDialogElement } from "@theme/utilities";
import { show, hide } from "@theme/utility-storage";
import { log, createReporter } from "@theme/logger";
import {
  clearPeakSaverEnrollmentAttributes,
  persistEnrollmentAttributes,
} from "@theme/peak-saver-claims";
import { safeSubmit } from "@theme/mysa-utilities";
import {
  BCH_OFFER_KEY_PS20_FREE_THERMOSTAT,
  fillTemplate,
  formatAddressLine,
  scoreSites,
} from "@theme/utility-sso-eligibility";
import {
  applyPeakSaverStepCopy,
  readPeakSaverSsoCopy,
} from "@theme/peak-saver-sso-copy";
import {
  buildBaseEnrollmentData,
  cancelSSOAuthWatch,
  handleSSOCallbackMessage,
  openSSOAuthInNewTab,
  parseSSOResponse,
  startSSOAuthFlow,
} from "@theme/utility-sso-flow";
import { renderAddressRows } from "@theme/utility-sso-address-ui";
import { injectEnrollmentInputs as injectEnrollmentFormInputs } from "@theme/sso-checkout-interceptor";

const reportError = createReporter("SSO");

/**
 * @typedef {"login" | "address" | "exhausted" | "ineligible" | "verified" | "error"} SSOStep
 */

/**
 * @extends {Component}
 */
class SSOHandler extends Component {
  /** @type {{ ssoCallback: (e: MessageEvent) => void, clearSsoState: () => void } | null} */
  #windowHandlers = null;

  /** @type {SSOHandlerRefs} */
  // @ts-ignore - refs are collected by Component base class
  refs;

  /** @type {number | null} */
  availableUnits = null;

  /** @type {SSOStep} */
  #step = "login";

  /** @type {"available" | "exhausted" | "ineligible" | null} */
  #outcome = null;

  /** @type {import('@theme/utility-sso-eligibility').ScoredSites | null} */
  #scored = null;

  /** @type {boolean} */
  #booted = false;

  connectedCallback() {
    super.connectedCallback();

    // Peak Saver section morph parks <peak-saver-cart>, which disconnects this
    // node briefly. Re-init must not wipe enrollmentData (verified UI would lie).
    if (this.#booted) {
      if (!this.#windowHandlers) this.#bindWindowEvents();
      log("[SSO] SSOHandler reconnected (enrollment preserved)");
      return;
    }
    this.#booted = true;

    this.providerCode = this.getAttribute("data-utility-provider") || "";
    this.isMobile =
      /iPhone|iPad|iPod|Android|webOS|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent,
      );

    this.userProfile = {
      profileId: "",
      firstName: "",
      lastName: "",
      emailAddress: "",
      customerSites: [],
    };

    this.selectedSite = null;
    this.sessionId = null;
    this.isProcessing = false;
    this.useMockData = this.dataset.useMockData === "true";
    this.pendingAuthUrl = null;

    this.signInLabel =
      this.dataset.enrollLabel || "Sign in with BC Hydro account";
    this.confirmLabel = this.dataset.confirmLabel || "Confirm this address";
    this.checkAnotherLabel =
      this.dataset.checkAnotherLabel || "Check another address";

    this.copy = readPeakSaverSsoCopy(this.dataset);

    /** @type {Record<string, string> | null} */
    this.enrollmentData = null;

    this.#bindWindowEvents();
    this.#updateSignInButtonState();

    log("[SSO] SSOHandler connected:", this.providerCode);
  }

  /** @type {{ form: HTMLFormElement, submitter: HTMLElement | null } | null} */
  #pendingCheckout = null;

  disconnectedCallback() {
    // Parent <peak-saver-cart> morph-park moves the whole subtree; keep listeners
    // and enrollment so checkout still sees hasEnrollmentData() after reinsert.
    if (this.closest("[data-morph-island]")?.dataset.morphPark === "true") {
      return;
    }

    super.disconnectedCallback();
    cancelSSOAuthWatch(this);
    this.#unbindWindowEvents();
    log("[SSO] SSOHandler disconnected");
  }

  /**
   * @returns {HTMLInputElement | null}
   */
  get termsAcceptanceInput() {
    return /** @type {HTMLInputElement | null} */ (
      this.querySelector('[ref="termsCheckbox"] input[type="checkbox"]')
    );
  }

  /**
   * Heating-system acknowledgment — buyer confirms baseboard / fan-forced heat.
   * Gates sign-in alongside terms so ineligible heat types self-select out.
   * @returns {HTMLInputElement | null}
   */
  get heatingAcknowledgmentInput() {
    return /** @type {HTMLInputElement | null} */ (
      this.querySelector('[ref="heatingCheckbox"] input[type="checkbox"]')
    );
  }

  /** @returns {boolean} Both eligibility gates are checked. */
  get #eligibilityConfirmed() {
    return Boolean(
      this.termsAcceptanceInput?.checked &&
      this.heatingAcknowledgmentInput?.checked,
    );
  }

  // —— Declarative Component handlers (on:*) ——

  /**
   * @param {MouseEvent} event
   */
  async handleSignInClick(event) {
    event.preventDefault();
    const button = this.refs.enrollNowButton;
    if (!button || button.disabled || this.isProcessing) return;
    if (!this.#eligibilityConfirmed) return;

    if (this.useMockData && window.UtilityBlockMockData) {
      log("[SSO] Using mock data");
      this.#applyParsedSSO(
        parseSSOResponse(window.UtilityBlockMockData.generateSSOResponse()),
      );
      return;
    }

    await this.#startSSOFlow();
  }

  /**
   * @param {MouseEvent} event
   */
  handleConfirmClick(event) {
    event.preventDefault();
    const button = this.refs.confirmAddressButton;
    if (!button || button.disabled || this.isProcessing) return;
    if (this.#step === "exhausted") {
      this.resetState();
      return;
    }
    if (!this.selectedSite || (this.availableUnits ?? 0) <= 0) return;
    void this.#saveUtilityInfo();
  }

  /**
   * @param {MouseEvent} event
   */
  handleLogoutClick(event) {
    event.preventDefault();
    this.resetState();
  }

  /**
   * @param {Event} event
   */
  handleFormChange(event) {
    // Component proxies event.target to the on: host; use composedPath for the real node.
    const pathTarget = /** @type {EventTarget | null} */ (
      event.composedPath?.()[0] ?? event.target
    );
    const input = pathTarget instanceof HTMLInputElement ? pathTarget : null;

    // Always sync — host-level on:change makes event.target the <sso-handler>.
    this.#updateSignInButtonState();

    if (
      input?.name === "address-selector" &&
      input.type === "radio" &&
      input.checked
    ) {
      this.#handleAddressSelection(input);
    }
  }

  /**
   * @param {MouseEvent} event
   */
  handleDialogClick(event) {
    const dialog = this.refs.addressRequiredDialog;
    if (!dialog) return;

    // Component proxies event.target to the on: host; use composedPath for the real node.
    const pathTarget = /** @type {Element | null} */ (
      event.composedPath?.()[0] ?? null
    );
    if (!(pathTarget instanceof Element)) return;

    if (
      pathTarget === dialog ||
      pathTarget.classList.contains(
        "utility-sso__address-required-dialog-wrapper",
      )
    ) {
      this.#closeAddressRequiredModal();
      return;
    }

    if (pathTarget.closest("[ref='addressRequiredCloseButton']")) {
      event.preventDefault();
      this.#closeAddressRequiredModal();
      return;
    }

    if (pathTarget.closest("[ref='addressRequiredContinueButton']")) {
      event.preventDefault();
      this.#closeAddressRequiredModal();
      this.resumePendingCheckout();
    }
  }

  /**
   * @param {MouseEvent} event
   */
  handleRetryClick(event) {
    event.preventDefault();
    event.stopPropagation();
    this.#setStep("login");
    this.isProcessing = false;
  }

  /**
   * @param {MouseEvent} event
   */
  handleContinueInTabClick(event) {
    event.preventDefault();
    this.#openInNewTab();
  }

  // —— Checkout public API ——

  /**
   * @param {HTMLFormElement} form
   */
  injectEnrollmentInputs(form) {
    injectEnrollmentFormInputs(form, this.enrollmentData);
  }

  /**
   * @returns {boolean}
   */
  hasEnrollmentData() {
    return this.enrollmentData !== null;
  }

  /**
   * @param {HTMLFormElement} form
   * @param {HTMLElement | null} submitter
   */
  prepareBlockedCheckout(form, submitter) {
    this.#pendingCheckout = { form, submitter };
    this.showAddressRequiredModal();
  }

  /**
   * Resume a blocked checkout only with live enrollment + `_dr_*` inject.
   * Without enrollment, drop the pending submit so the buyer can finish SSO.
   */
  resumePendingCheckout() {
    if (!this.#pendingCheckout) return;

    if (!this.hasEnrollmentData()) {
      this.#pendingCheckout = null;
      log("[SSO] resumePendingCheckout ignored — enrollment required");
      return;
    }

    const { form, submitter } = this.#pendingCheckout;
    this.#pendingCheckout = null;
    this.injectEnrollmentInputs(form);
    try {
      safeSubmit(form, submitter);
    } catch {
      safeSubmit(form);
    }
  }

  showAddressRequiredModal() {
    const dialog = /** @type {HTMLDialogElement | null} */ (
      this.refs.addressRequiredDialog
    );
    if (!dialog) {
      log("[SSO] Address required dialog not found");
      return;
    }
    if (dialog.open) return;
    if (!showDialogElement(dialog)) return;
    log("[SSO] Address required modal shown");
  }

  /** Drop the in-memory session and return the step to login. Does not touch the cart. */
  dropSession() {
    this.userProfile = {
      profileId: "",
      firstName: "",
      lastName: "",
      emailAddress: "",
      customerSites: [],
    };

    this.enrollmentData = null;
    this.availableUnits = null;
    this.selectedSite = null;
    this.sessionId = null;
    this.isProcessing = false;
    this.pendingAuthUrl = null;
    this.#pendingCheckout = null;
    cancelSSOAuthWatch(this);

    this.#setStep("login");
  }

  resetState() {
    const hadEnrollment = this.enrollmentData !== null;
    this.dropSession();

    if (hadEnrollment) {
      this.dispatchEvent(
        new CustomEvent(MysaEvents.utilitySsoCleared, {
          bubbles: true,
          detail: { availableUnits: null },
        }),
      );
      // Peak Saver cart owns line + attr clear. A parallel attrs wipe here
      // overlapped that `/cart/update.js` and 422'd.
      const cartHost = document.querySelector("peak-saver-cart");
      if (!(cartHost instanceof HTMLElement) || cartHost.hidden) {
        void this.#clearEnrollmentAttributes();
      }
    }
  }

  // —— Private: window listeners (non-declarative) ——

  #bindWindowEvents() {
    this.#windowHandlers = {
      ssoCallback: (e) => this.#handleSSOCallback(e),
      clearSsoState: () => {
        log("[SSO] Utility partner changed, clearing SSO state");
        this.resetState();
      },
      checkoutReturn: () => this.dropSession(),
    };

    window.addEventListener("message", this.#windowHandlers.ssoCallback);
    document.addEventListener(
      MysaEvents.utilityPartnerChanged,
      this.#windowHandlers.clearSsoState,
    );
    document.addEventListener(
      MysaEvents.utilityPartnerCleared,
      this.#windowHandlers.clearSsoState,
    );
    document.addEventListener(
      MysaEvents.utilitySsoCheckoutReturn,
      this.#windowHandlers.checkoutReturn,
    );
  }

  #unbindWindowEvents() {
    if (!this.#windowHandlers) return;

    window.removeEventListener("message", this.#windowHandlers.ssoCallback);
    document.removeEventListener(
      MysaEvents.utilityPartnerChanged,
      this.#windowHandlers.clearSsoState,
    );
    document.removeEventListener(
      MysaEvents.utilityPartnerCleared,
      this.#windowHandlers.clearSsoState,
    );
    document.removeEventListener(
      MysaEvents.utilitySsoCheckoutReturn,
      this.#windowHandlers.checkoutReturn,
    );
    this.#windowHandlers = null;
  }

  // —— Private: step / error UI ——

  /**
   * @param {SSOStep} step
   * @param {{ hideAddressFooter?: boolean }} [options]
   */
  #setStep(step, { hideAddressFooter = false } = {}) {
    this.#step = step;
    this.dataset.step = step;

    const showLogin = step === "login";
    const showAddress =
      step === "address" ||
      step === "exhausted" ||
      step === "ineligible" ||
      step === "error";
    const showVerified = step === "verified";

    if (showLogin) {
      show(this.refs.loginStep);
    } else {
      hide(this.refs.loginStep);
    }

    if (showAddress) {
      show(this.refs.addressSelectorInner);
      show(this.refs.addressSelector);
    } else {
      hide(this.refs.addressSelectorInner);
      hide(this.refs.addressSelector);
    }

    if (showVerified) {
      show(this.refs.successMessageInner);
    } else {
      hide(this.refs.successMessageInner);
    }

    // Match v1: error templates replace the address UI — hide Select Address
    // heading and Confirm footer (same role as hideInstruction: true).
    if (this.refs.stepHeader) {
      this.refs.stepHeader.hidden = step === "error";
    }

    if (this.refs.addressFooter) {
      const ineligible = step === "ineligible";
      const divided = ineligible || step === "exhausted" || step === "address";
      this.refs.addressFooter.hidden = step === "error" || hideAddressFooter;
      this.refs.addressFooter.classList.toggle(
        "utility-sso__panel-footer--ineligible",
        ineligible,
      );
      this.refs.addressFooter.classList.toggle(
        "utility-sso__panel-footer--divided",
        divided,
      );
    }

    if (this.refs.addressPanelLabel) {
      this.refs.addressPanelLabel.hidden =
        step !== "address" && step !== "exhausted";
    }

    if (step === "login") {
      this.#outcome = null;
      this.#scored = null;
      if (this.refs.addressSelectorContent) {
        this.refs.addressSelectorContent.innerHTML = "";
      }
      this.#setExhaustedWarning(false);
      this.refs.addressSelector?.classList.remove(
        "utility-sso__address-selector--exhausted",
      );
      if (this.termsAcceptanceInput) {
        this.termsAcceptanceInput.checked = false;
      }
      // Both acknowledgments gate sign-in via #eligibilityConfirmed — clear the
      // heating attestation too so logout/retry cannot leave it ticked.
      if (this.heatingAcknowledgmentInput) {
        this.heatingAcknowledgmentInput.checked = false;
      }
      this.#updateSignInButtonState();
      if (this.refs.confirmAddressButton) {
        this.refs.confirmAddressButton.disabled = true;
        this.#setConfirmLabel(this.confirmLabel);
      }
    }

    // Error copy lives inside the mounted template — don't paint address heading
    if (step !== "error") {
      this.#setStepCopy(step);
    }

    this.dispatchEvent(
      new CustomEvent(MysaEvents.utilitySsoStep, {
        bubbles: true,
        detail: { step, outcome: this.#stepOutcome() },
      }),
    );
  }

  /**
   * @param {SSOStep} step
   */
  #setStepCopy(step) {
    applyPeakSaverStepCopy({
      copy: this.copy,
      stepHeading: this.refs.stepHeading,
      stepBody: this.refs.stepBody,
      step,
      availableUnits: this.availableUnits,
    });
  }

  /** @returns {"available" | "exhausted" | "ineligible" | null} */
  #stepOutcome() {
    return this.#outcome;
  }

  /** @param {boolean} showWarning */
  #setExhaustedWarning(showWarning) {
    const el = this.refs.exhaustedWarning;
    const copy = this.refs.exhaustedWarningCopy;
    if (!el) return;
    if (!showWarning || !this.copy.exhaustedWarning) {
      el.hidden = true;
      copy?.replaceChildren();
      return;
    }
    if (copy) copy.innerHTML = this.copy.exhaustedWarning;
    else el.innerHTML = this.copy.exhaustedWarning;
    el.hidden = false;
  }

  /** @param {string} label */
  #setConfirmLabel(label) {
    const button = this.refs.confirmAddressButton;
    if (!button) return;
    const labelEl = button.querySelector(".button-text");
    if (labelEl) labelEl.textContent = label;
    else button.textContent = label;
  }

  /**
   * @param {Object} options
   * @param {HTMLTemplateElement | null} [options.template]
   * @param {Node | null} [options.node]
   * @param {string} [options.alertFallback]
   * @param {boolean} [options.bindContinueInTab]
   * @param {SSOStep} [options.step]
   */
  #mountError({
    template = null,
    node = null,
    alertFallback = "",
    bindContinueInTab = false,
    step = "error",
  } = {}) {
    const { addressSelectorContent } = this.refs;

    if (!addressSelectorContent) {
      if (alertFallback) alert(alertFallback);
      return;
    }

    addressSelectorContent.innerHTML = "";

    if (template) {
      const clone = template.content.cloneNode(true);
      if (bindContinueInTab) {
        const newTabBtn = /** @type {DocumentFragment} */ (clone).querySelector(
          ".js-continue-in-tab",
        );
        newTabBtn?.setAttribute(
          "on:click",
          "sso-handler/handleContinueInTabClick",
        );
      }
      addressSelectorContent.appendChild(clone);
    } else if (node) {
      addressSelectorContent.appendChild(node);
    }

    this.#setStep(step);
  }

  /**
   * @param {string} message
   */
  #showMessageError(message) {
    const wrapperEl = document.createElement("div");
    wrapperEl.className = "utility-sso__error-state";

    const messageEl = document.createElement("p");
    messageEl.className = "utility-sso__address-message";
    messageEl.textContent = message;
    wrapperEl.appendChild(messageEl);

    const retryBtn = document.createElement("button");
    retryBtn.type = "button";
    retryBtn.className = "button as-primary as-black";
    retryBtn.setAttribute("on:click", "sso-handler/handleRetryClick");
    const label = document.createElement("span");
    label.className = "button-text";
    label.textContent = "Try again";
    retryBtn.appendChild(label);
    wrapperEl.appendChild(retryBtn);

    this.#mountError({
      node: wrapperEl,
      alertFallback: message,
    });
  }

  #showTechnicalErrorMessage() {
    this.#mountError({
      template: this.refs.technicalErrorTemplate ?? null,
      alertFallback:
        "We're having trouble connecting. Please try again or contact support if the issue persists.",
    });
  }

  #showNoEligibleAccountsMessage() {
    this.#outcome = "ineligible";
    this.#setExhaustedWarning(false);
    this.#mountError({
      template: this.refs.noAccountsTemplate ?? null,
      step: "ineligible",
    });
  }

  #showPopupBlockedMessage() {
    if (!this.refs.popupBlockedTemplate) {
      const proceed = confirm(
        "Pop-up window was blocked. Click OK to open in a new tab instead.",
      );
      if (proceed && this.pendingAuthUrl) this.#openInNewTab();
      return;
    }

    this.#mountError({
      template: this.refs.popupBlockedTemplate,
      bindContinueInTab: true,
    });
  }

  // —— Private: auth flow ——

  #ssoFlowHooks() {
    return {
      onTechnicalError: () => this.#showTechnicalErrorMessage(),
      onMessageError: (message) => this.#showMessageError(message),
      onPopupBlocked: () => this.#showPopupBlockedMessage(),
    };
  }

  async #startSSOFlow() {
    await startSSOAuthFlow(this, this.#ssoFlowHooks());
  }

  #openInNewTab() {
    openSSOAuthInNewTab(this, this.#ssoFlowHooks());
  }

  /**
   * @param {MessageEvent} event
   */
  #handleSSOCallback(event) {
    handleSSOCallbackMessage(this, event, (parsed) => {
      this.#applyParsedSSO(parsed);
    });
  }

  /**
   * @param {ReturnType<typeof parseSSOResponse>} parsed
   */
  #applyParsedSSO(parsed) {
    if (!parsed.ok) {
      if (parsed.kind === "technical") this.#showTechnicalErrorMessage();
      else this.#showMessageError(parsed.message);
      return;
    }

    this.userProfile = parsed.profile;
    this.sessionId = parsed.sessionId;
    this.#renderAddressSelector(this.userProfile.customerSites);
  }

  // —— Private: address / enrollment ——

  /**
   * @param {Array<Record<string, any>>} sites
   */
  #renderAddressSelector(sites) {
    const {
      addressSelectorContent,
      addressItemTemplate,
      addressStaticTemplate,
    } = this.refs;

    if (!addressSelectorContent || !addressItemTemplate) {
      console.warn("[SSO] Address selector elements not found");
      return;
    }

    this.selectedSite = null;
    const scored = scoreSites(sites);
    this.#scored = scored;
    this.#outcome = scored.outcome;

    if (scored.ineligibleOnly) {
      this.refs.addressSelector?.classList.remove(
        "utility-sso__address-selector--exhausted",
      );
      this.#showNoEligibleAccountsMessage();
      return;
    }

    this.refs.addressSelector?.classList.toggle(
      "utility-sso__address-selector--exhausted",
      scored.exhaustedOnly,
    );
    this.#setExhaustedWarning(scored.exhaustedOnly);

    renderAddressRows({
      container: addressSelectorContent,
      template: addressItemTemplate,
      staticTemplate: addressStaticTemplate,
      rows: scored.rows,
      providerCode: this.providerCode,
      displayOnly: scored.displayOnly,
      copy: {
        statusAvailable: this.copy.statusAvailable,
        statusExhausted: this.copy.statusExhausted,
        statusIneligible: this.copy.statusIneligible,
      },
    });

    this.#setStep(scored.exhaustedOnly ? "exhausted" : "address");

    if (scored.firstAvailableIndex >= 0) {
      const row = scored.rows[scored.firstAvailableIndex];
      this.selectedSite = row.site;
      this.availableUnits = row.units;

      if (!scored.displayOnly) {
        const firstRadio = /** @type {HTMLInputElement | null} */ (
          addressSelectorContent.querySelector(
            `input[name="address-selector"][value="${scored.firstAvailableIndex}"]`,
          )
        );
        if (firstRadio) firstRadio.checked = true;
      }
    }

    this.#updateConfirmButtonState();
  }

  /**
   * @param {HTMLInputElement} target
   */
  #handleAddressSelection(target) {
    if (target.disabled) return;
    if (target.dataset.eligibility !== "available") {
      target.checked = false;
      return;
    }

    const selectedIndex = Number.parseInt(target.value, 10);
    const row = this.#scored?.rows.find(
      (entry) => entry.index === selectedIndex,
    );
    if (!row) return;

    this.selectedSite = row.site;
    this.availableUnits = row.units;
    this.#updateConfirmButtonState();
  }

  async #saveUtilityInfo() {
    if (!this.selectedSite || !this.userProfile || this.isProcessing) return;

    this.isProcessing = true;
    try {
      const selectedSite = this.selectedSite;
      const units = this.availableUnits;
      if (units == null || units <= 0) return;

      const partnerCode = this.dataset.utilityProvider || "bc-hydro";

      this.availableUnits = units;
      // Keys must match utility-incentive-discounts GraphQL aliases / resolvePeakSaverFreeOffer.
      // `_dr_free_thermostat_offer` is required (PS20FreeTH) — without it, price_floor 0
      // lines are blocked and no free discount applies.
      this.enrollmentData = {
        ...buildBaseEnrollmentData({
          site: selectedSite,
          profile: this.userProfile,
        }),
        _dr_free_thermostat_offer: BCH_OFFER_KEY_PS20_FREE_THERMOSTAT,
        _dr_available_units: String(units),
        _utility_partner_code: partnerCode,
      };
      // SSO bridge session id for downstream Bridge / order consumers.
      if (this.sessionId != null && this.sessionId !== "") {
        this.enrollmentData._dr_sso_session = String(this.sessionId);
      }

      try {
        await this.#persistEnrollmentAttributes();
      } catch (error) {
        reportError(error, { tags: { op: "sso-persist-attrs" } });
        console.error("[SSO] Failed to persist enrollment attributes:", error);
        // Still unlock UI — checkout inject remains a fallback.
      }

      this.#showVerifiedState();

      this.dispatchEvent(
        new CustomEvent(MysaEvents.utilitySsoEnrolled, {
          bubbles: true,
          detail: { availableUnits: units },
        }),
      );

      log("[SSO] Enrollment data stored:", this.enrollmentData);
    } finally {
      this.isProcessing = false;
    }
  }

  /**
   * Write Peak Saver / DR stamps to cart attributes so the discount Function
   * sees them before checkout (not only via form `attributes[...]` inject).
   */
  async #persistEnrollmentAttributes() {
    if (!this.enrollmentData) return;
    await persistEnrollmentAttributes(this.enrollmentData);
    log("[SSO] Cart enrollment attributes persisted");
  }

  /** Clear theme-owned Peak Saver / SSO stamps on logout. */
  async #clearEnrollmentAttributes() {
    try {
      await clearPeakSaverEnrollmentAttributes();
    } catch (error) {
      reportError(error, { tags: { op: "sso-clear-attrs" } });
      console.error("[SSO] Failed to clear enrollment attributes:", error);
    }
  }

  #showVerifiedState() {
    this.#setStep("verified");

    const site = this.selectedSite;
    const units = this.availableUnits ?? 0;
    const addressLine = formatAddressLine(site);

    if (this.refs.verifiedTitle) {
      this.refs.verifiedTitle.textContent = this.copy.verifiedTitle;
    }

    if (this.refs.verifiedAddress) {
      this.refs.verifiedAddress.textContent = fillTemplate(
        this.copy.verifiedAddressTemplate,
        { address: addressLine },
      );
    }

    if (this.refs.verifiedUnitsPill) {
      const unitsText = fillTemplate(this.copy.verifiedUnitsTemplate, {
        count: units,
      });
      this.refs.verifiedUnitsPill.replaceChildren();
      const countMatch = String(unitsText).match(/^(\d+)(.*)$/);
      if (countMatch) {
        const countEl = document.createElement("span");
        countEl.className = "utility-sso__units-count";
        countEl.textContent = countMatch[1];
        this.refs.verifiedUnitsPill.append(
          countEl,
          document.createTextNode(countMatch[2]),
        );
      } else {
        this.refs.verifiedUnitsPill.textContent = unitsText;
      }
    }

    const logoutEnabled = Theme.features?.sso_logout_enabled !== false;
    if (this.refs.verifiedLogoutButton) {
      this.refs.verifiedLogoutButton.hidden = !logoutEnabled;
    }
  }

  #closeAddressRequiredModal() {
    const dialog = this.refs.addressRequiredDialog;
    if (dialog) closeDialogElement(dialog);
  }

  #updateSignInButtonState() {
    const button = this.refs.enrollNowButton;
    if (!button) return;
    const confirmed = this.#eligibilityConfirmed;
    button.disabled = !confirmed;
    button.classList.toggle("as-secondary", !confirmed);
    button.classList.toggle("as-primary", confirmed);
    const labelEl = button.querySelector(".button-text");
    if (labelEl) labelEl.textContent = this.signInLabel;
    else button.textContent = this.signInLabel;
  }

  #updateConfirmButtonState() {
    const button = this.refs.confirmAddressButton;
    if (!button) return;

    if (this.#step === "exhausted") {
      button.disabled = false;
      this.#setConfirmLabel(this.checkAnotherLabel);
      return;
    }

    const eligibleSelected =
      this.selectedSite != null && (this.availableUnits ?? 0) > 0;

    button.disabled = !eligibleSelected;
    this.#setConfirmLabel(this.confirmLabel);
  }
}

if (!customElements.get("sso-handler")) {
  customElements.define("sso-handler", SSOHandler);
}

export { SSOHandler, BCH_OFFER_KEY_PS20_FREE_THERMOSTAT };
